import React, { useEffect, useState } from "react";
import { Loader2, Upload, X, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { adminCreateProduct, adminUpdateProduct, adminUploadImage, fileUrl } from "../api";
import { compressImage } from "../lib/compressImage";

const CATEGORIES = ["Springkussens", "Glijbanen", "Stormbanen"];
const EMPTY = {
  name: "", category: "Springkussens", price: "", tagline: "", description: "",
  capacity: "", size: "", height: "", opzettijd: "± 15 min", leeftijd: "Alle leeftijden",
  personen: "", themas: "", images: [],
};

export const AdminProductDialog = ({ open, onClose, token, product, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || "", category: product.category || "Springkussens", price: String(product.price ?? ""),
        tagline: product.tagline || "", description: product.description || "", capacity: product.capacity || "",
        size: product.size || "", height: product.height || "", opzettijd: product.opzettijd || "± 15 min",
        leeftijd: product.leeftijd || "Alle leeftijden", personen: String(product.personen ?? ""),
        themas: (product.themas || []).join(", "), images: product.images || [],
      });
    } else {
      setForm(EMPTY);
    }
  }, [product, open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const compressed = await compressImage(f);
        const res = await adminUploadImage(compressed, token);
        setForm((p) => ({ ...p, images: [...p.images, res.url] }));
      }
    } catch (err) {
      toast({ title: "Upload mislukt", description: err?.response?.data?.detail || "Probeer een andere afbeelding." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) => setForm((p) => ({ ...p, images: p.images.filter((u) => u !== url) }));
  const makeCover = (url) => setForm((p) => ({ ...p, images: [url, ...p.images.filter((u) => u !== url)] }));

  const save = async () => {
    if (!form.name || !form.price) { toast({ title: "Vul minimaal naam en prijs in" }); return; }
    if (!form.images.length) { toast({ title: "Voeg minstens één foto toe" }); return; }
    setSaving(true);
    const payload = {
      name: form.name, category: form.category, price: Number(form.price), tagline: form.tagline,
      description: form.description, capacity: form.capacity, size: form.size, height: form.height,
      opzettijd: form.opzettijd, leeftijd: form.leeftijd,
      personen: form.personen ? Number(form.personen) : 8,
      themas: form.themas ? form.themas.split(",").map((t) => t.trim()).filter(Boolean) : [],
      images: form.images,
    };
    try {
      if (product) await adminUpdateProduct(product.slug, payload, token);
      else await adminCreateProduct(payload, token);
      toast({ title: product ? "Springkussen bijgewerkt" : "Springkussen toegevoegd" });
      onSaved();
      onClose();
    } catch (err) {
      toast({ title: "Opslaan mislukt", description: err?.response?.data?.detail || "Probeer opnieuw." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-brand-navy">{product ? "Springkussen bewerken" : "Nieuw springkussen"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Images */}
          <div>
            <Label className="font-semibold text-brand-navy">Foto's</Label>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.images.map((url, i) => (
                <div key={url} className="relative w-24 h-24 rounded-xl overflow-hidden group border border-slate-200">
                  <img src={fileUrl(url)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute top-1 left-1 bg-brand-blue text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">Cover</span>}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {i !== 0 && <button type="button" onClick={() => makeCover(url)} title="Maak cover" className="w-7 h-7 rounded-full bg-white text-brand-navy flex items-center justify-center"><Star className="w-3.5 h-3.5" /></button>}
                    <button type="button" onClick={() => removeImage(url)} data-testid="product-remove-image" title="Verwijder" className="w-7 h-7 rounded-full bg-white text-brand-pink flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              <label className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:border-brand-blue hover:text-brand-blue transition-colors" data-testid="product-upload-label">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                <span className="text-[11px] mt-1">Upload</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="product-image-input" />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold text-brand-navy">Naam *</Label>
              <Input data-testid="product-name-input" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="bv. Regenboog springkussen" />
            </div>
            <div>
              <Label className="font-semibold text-brand-navy">Categorie *</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="font-semibold text-brand-navy">Prijs per dag (€) *</Label>
              <Input data-testid="product-price-input" type="number" min="0" className="mt-1.5" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="85" />
            </div>
            <div>
              <Label className="font-semibold text-brand-navy">Capaciteit</Label>
              <Input data-testid="product-capacity-input" className="mt-1.5" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="8-10 kinderen" />
            </div>
            <div>
              <Label className="font-semibold text-brand-navy">Max. personen</Label>
              <Input type="number" min="0" className="mt-1.5" value={form.personen} onChange={(e) => set("personen", e.target.value)} placeholder="8" />
            </div>
          </div>

          <div>
            <Label className="font-semibold text-brand-navy">Korte slogan</Label>
            <Input data-testid="product-tagline-input" className="mt-1.5" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Het vrolijkste kasteel voor elk feest" />
          </div>
          <div>
            <Label className="font-semibold text-brand-navy">Omschrijving</Label>
            <Textarea className="mt-1.5" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Beschrijf het springkussen..." />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="font-semibold text-brand-navy">Afmeting</Label>
              <Input className="mt-1.5" value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="4 x 4 m" />
            </div>
            <div>
              <Label className="font-semibold text-brand-navy">Hoogte</Label>
              <Input className="mt-1.5" value={form.height} onChange={(e) => set("height", e.target.value)} placeholder="3,5 m" />
            </div>
            <div>
              <Label className="font-semibold text-brand-navy">Leeftijd</Label>
              <Input className="mt-1.5" value={form.leeftijd} onChange={(e) => set("leeftijd", e.target.value)} placeholder="Alle leeftijden" />
            </div>
          </div>
          <div>
            <Label className="font-semibold text-brand-navy">Thema's (komma-gescheiden)</Label>
            <Input className="mt-1.5" value={form.themas} onChange={(e) => set("themas", e.target.value)} placeholder="Prinses, Regenboog, Unicorn" />
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 rounded-full border-2 border-slate-200 text-brand-navy font-bold py-3 hover:bg-slate-50 transition-colors">Annuleren</button>
            <button onClick={save} disabled={saving || uploading} data-testid="product-save-button" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold py-3 transition-colors disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {product ? "Opslaan" : "Toevoegen"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminProductDialog;
